import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { placeCharm } from '@clasplab/look-stage';

const clTrack = (name, data) => {
  try {
    // @ts-ignore Shopify analytics global; custom events reach Customer Events pixels (Meta, Clarity…)
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {}
};

/**
 * Charm preview: band → colour → charm, shown together on the band's photo, then add both to cart.
 * Data comes from the section's JSON script (bands with placement, charms with cut-out URLs).
 */
class ClaspLabCharmPreview extends HTMLElement {
  /** @type {{bands: any[], charms: any[]}} */
  #data = { bands: [], charms: [] };
  #band = 0;
  #variant = 0;
  /** @type {number | null} */
  #charm = null;
  #busy = false;
  #started = false;
  #tag = 'all';
  #expanded = false;
  /** Charms shown before "Show all" */
  static LIMIT = 16;

  #markStarted() {
    if (this.#started) return;
    this.#started = true;
    clTrack('build_look_started', { source: 'charm_preview' });
  }

  connectedCallback() {
    const json = this.querySelector('script[ref="data"]');
    try {
      this.#data = JSON.parse(json?.textContent || '{}');
    } catch (e) {
      console.error('Charm preview data', e);
      return;
    }
    this.addEventListener('click', this.#onClick);
    window.addEventListener('resize', () => this.#placeCharm());
    // Picking a band greys out unconfirmed charms and picks the first confirmed one (#applyFit)
    this.#pickBand(0);
    this.#fromUrl();
    this.#filter('all');
  }

  /**
   * Links like ?band=knit&colour=Bone&charm=charm-bar-croissant (from the quiz) pick that look.
   * Without a link, the shopper's last quiz look (localStorage cl-look, 30 days) is picked, so the
   * builder never starts from zero after the quiz.
   */
  #fromUrl() {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('band') && !params.get('charm')) {
      try {
        const saved = JSON.parse(localStorage.getItem('cl-look') || 'null');
        if (saved && Date.now() - saved.at < 30 * 864e5) {
          params.set('band', saved.band);
          params.set('colour', saved.colour);
          params.set('charm', saved.charm);
        }
      } catch (e) {}
    }
    const band = this.#data.bands.findIndex((b) => b.handle === params.get('band'));
    if (band >= 0) {
      this.#pickBand(band);
      const colour = params.get('colour');
      const variant = colour ? this.#data.bands[band].variants.findIndex((/** @type {any} */ v) => v.title === colour) : -1;
      if (variant >= 0) this.#pickVariant(variant);
    }
    const charm = this.#data.charms.findIndex((c) => c.handle === params.get('charm'));
    if (charm >= 0) this.#pickCharm(charm);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const band = t.closest('[data-band]');
    const colour = t.closest('[data-variant]');
    const charm = t.closest('[data-charm]');
    const filter = t.closest('[data-filter]');
    if (band instanceof HTMLElement || colour instanceof HTMLElement || charm instanceof HTMLElement) this.#markStarted();
    if (band instanceof HTMLElement) {
      this.#pickBand(Number(band.dataset.band));
      clTrack('band_selected', { band: this.#data.bands[this.#band]?.title });
    } else if (colour instanceof HTMLElement) {
      this.#pickVariant(Number(colour.dataset.variant));
      const b = this.#data.bands[this.#band];
      clTrack('band_selected', { band: b?.title, colour: b?.variants[this.#variant]?.title });
    } else if (charm instanceof HTMLElement) {
      this.#pickCharm(Number(charm.dataset.charm));
      clTrack('charm_selected', { charm: this.#data.charms[Number(charm.dataset.charm)]?.title, source: 'charm_preview' });
    }
    else if (filter instanceof HTMLElement) {
      this.#expanded = false;
      this.#filter(filter.dataset.filter || 'all');
    } else if (t.closest('[data-more]')) {
      this.#expanded = true;
      this.#filter(this.#tag);
    }
    else if (t.closest('[ref="add"]')) this.#addToCart();
  };

  /** @param {string} name */
  #ref(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[ref="${name}"]`));
  }

  /** @param {number} index */
  #pickBand(index) {
    const band = this.#data.bands[index];
    if (!band) return;
    this.#band = index;
    this.querySelectorAll('[data-band]').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.getAttribute('data-band') === String(index)))
    );
    const colours = this.#ref('colours');
    if (colours) {
      colours.innerHTML = band.variants
        .map(
          (/** @type {any} */ v, /** @type {number} */ i) =>
            `<button type="button" class="cl-cp__colour" data-variant="${i}" aria-pressed="false"${v.available ? '' : ' disabled'}>` +
            `<img src="${v.thumb}" alt="" width="52" height="52" loading="lazy"><span>${this.#escape(v.title)}</span></button>`
        )
        .join('');
    }
    const first = band.variants.findIndex((/** @type {any} */ v) => v.available);
    this.#pickVariant(first === -1 ? 0 : first);
    this.#applyFit();
  }

  /** @param {any} charm */
  #fits(charm) {
    const band = this.#data.bands[this.#band];
    return Boolean(band && charm && (band.kinds || []).includes(charm.kind));
  }

  /**
   * Only charms whose type is physically confirmed on the picked band can be chosen (band.kinds from
   * snippets/cl-compat.liquid). The rest are disabled with a note; a picked charm that no longer fits
   * is swapped for the first one that does, or cleared.
   */
  #applyFit() {
    let anyBlocked = false;
    this.querySelectorAll('[data-charm]').forEach((b) => {
      if (!(b instanceof HTMLButtonElement)) return;
      const charm = this.#data.charms[Number(b.dataset.charm)];
      const fits = this.#fits(charm);
      if (!fits) anyBlocked = true;
      b.disabled = b.dataset.available === 'false' || !fits;
      b.title = fits ? charm?.title || '' : `${charm?.title || ''} · not available for this band`;
    });
    const note = this.querySelector('[data-fit-note]');
    if (note instanceof HTMLElement) note.hidden = !anyBlocked;

    const current = this.#charm === null ? null : this.#data.charms[this.#charm];
    if (!current || !this.#fits(current)) {
      const next = this.querySelector('[data-charm]:not(:disabled)');
      if (next instanceof HTMLElement) this.#pickCharm(Number(next.dataset.charm));
      else this.#clearCharm();
    }
  }

  #clearCharm() {
    this.#charm = null;
    this.querySelectorAll('[data-charm]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
    const img = /** @type {HTMLImageElement | null} */ (this.#ref('charmImg'));
    if (img) img.hidden = true;
    this.#render();
  }

  /** @param {number} index */
  #pickVariant(index) {
    const band = this.#data.bands[this.#band];
    const v = band?.variants[index];
    if (!v) return;
    this.#variant = index;
    this.querySelectorAll('[data-variant]').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.getAttribute('data-variant') === String(index)))
    );
    const img = /** @type {HTMLImageElement | null} */ (this.#ref('bandImg'));
    if (img) {
      img.src = v.image;
      img.alt = `${band.title} · ${v.title}`;
    }
    this.#placeCharm();
    this.#render();
  }

  /** @param {number} index */
  #pickCharm(index) {
    const c = this.#data.charms[index];
    if (!c || !this.#fits(c)) return;
    this.#charm = index;
    this.querySelectorAll('[data-charm]').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.getAttribute('data-charm') === String(index)))
    );
    const img = /** @type {HTMLImageElement | null} */ (this.#ref('charmImg'));
    if (img) {
      img.src = c.cutout;
      img.alt = c.title;
      img.hidden = false;
    }
    this.#placeCharm();
    this.#render();
  }

  /** @param {string} tag */
  #filter(tag) {
    this.#tag = tag;
    this.querySelectorAll('[data-filter]').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.getAttribute('data-filter') === tag))
    );
    // Matching charms, the picked one always kept visible; the rest wait behind "Show all"
    const limit = ClaspLabCharmPreview.LIMIT;
    let shown = 0;
    let matches = 0;
    this.querySelectorAll('[data-charm]').forEach((b) => {
      if (!(b instanceof HTMLElement)) return;
      const match = tag === 'all' || (b.dataset.tags || '').split(' ').includes(tag);
      const picked = b.getAttribute('aria-pressed') === 'true';
      if (match) matches += 1;
      const visible = match && (this.#expanded || shown < limit || picked);
      if (visible) shown += 1;
      b.hidden = !visible;
    });
    const more = this.querySelector('[data-more]');
    if (more instanceof HTMLElement) {
      more.hidden = this.#expanded || matches <= shown;
      more.textContent = `Show all ${matches} Claspies`;
    }
  }

  /** Position the charm on the band photo using the band's placement (all in % of the frame). */
  #placeCharm() {
    const band = this.#data.bands[this.#band];
    const c = this.#charm === null ? null : this.#data.charms[this.#charm];
    const img = /** @type {HTMLImageElement | null} */ (this.#ref('charmImg'));
    if (!band || !c || !img || !band.place) return;
    placeCharm(img, band.place, c.kind);
  }

  #render() {
    const band = this.#data.bands[this.#band];
    const v = band?.variants[this.#variant];
    const c = this.#charm === null ? null : this.#data.charms[this.#charm];
    const picked = this.#ref('picked');
    const total = this.#ref('total');
    const add = /** @type {HTMLButtonElement | null} */ (this.#ref('add'));
    if (picked && band && v) picked.textContent = `${band.title} · ${v.title}${c ? ` + ${c.title}` : ''}`;
    const cents = (v?.price || 0) + (c?.price || 0);
    if (total) total.textContent = this.#money(cents);
    if (add) add.disabled = !v || !v.available || !c || !this.#fits(c);
    // Pre-order notice (snippets/cl-preorder.liquid, part 'dynamic')
    const pre = this.querySelector('[data-cl-preorder]');
    if (pre instanceof HTMLElement) pre.hidden = !(band?.preorder || c?.preorder);
  }

  async #addToCart() {
    const band = this.#data.bands[this.#band];
    const v = band?.variants[this.#variant];
    const c = this.#charm === null ? null : this.#data.charms[this.#charm];
    const add = this.#ref('add');
    const error = this.#ref('error');
    if (this.#busy || !v || !c || !this.#fits(c)) return;
    this.#busy = true;
    add?.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    // @ts-ignore set by snippets/cl-preorder.liquid when pre-orders are on
    const preorder = window.ClaspLabPreorder;
    const line = (/** @type {any} */ item, /** @type {boolean} */ isPre) => (preorder ? preorder.line(item, isPre) : item);
    // Charm first: the cart lists the newest line on top, so the band leads the look in the cart
    const items = [line({ id: c.id, quantity: 1 }, c.preorder), line({ id: v.id, quantity: 1 }, band.preorder)];
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    try {
      // @ts-ignore Theme is a global (not on window) set by the theme's scripts snippet
      const routes = Theme.routes;
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cl-charm-preview',
          sourceId: 'cl-charm-preview',
          itemCount: 2,
          sections: response.sections,
          didError,
        },
      });
      if (!didError) {
        clTrack('build_look_added', {
          band: `${band.title} · ${v.title}`,
          charm: c.title,
          value: (v.price + c.price) / 100,
          currency: this.dataset.currency || 'EUR',
        });
      }
      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (error) {
          error.textContent = response.description || response.message || 'Could not add to cart.';
          error.hidden = false;
        }
      }
    } catch (e) {
      console.error(e);
      deferred.reject(e);
      if (error) {
        error.textContent = 'Could not add to cart. Please try again.';
        error.hidden = false;
      }
    } finally {
      this.#busy = false;
      add?.removeAttribute('aria-busy');
    }
  }

  /** @param {number} cents */
  #money(cents) {
    const value = cents / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /** @param {string} text */
  #escape(text) {
    const d = document.createElement('div');
    d.textContent = text;
    return d.innerHTML;
  }
}

if (!customElements.get('cl-charm-preview')) customElements.define('cl-charm-preview', ClaspLabCharmPreview);
